// Industry sample audit reports shown on the marketing landing page. Structure,
// section depth, KPI-overview columns (current / prev / 7d / 14d / target /
// status), guardrail breakdown tables and the analytical voice are modeled on
// REAL Soku ads-account-audit output (calibrated against live `soku ads`
// data + the product's ads-report format). All brands are fictional and all
// numbers are illustrative — no real customer data is reproduced.

export type ReportStatus =
  | 'Drawdown'
  | 'Critical'
  | 'Elevated'
  | 'Declining'
  | 'Improving'
  | 'Normal'
  | 'Flat'

export type KpiTile = {
  label: string
  value: string
  delta: string
  bad?: boolean
}

export type CompRow = {
  metric: string
  current: string
  previous: string
  avg7: string
  avg14: string
  target: string
  status: ReportStatus
}

export type Breakdown = {
  title: string
  // Optional one-line intro shown above the table (dimension tables use it).
  lede?: string
  columns: string[]
  rows: { name: string; cells: string[]; status: ReportStatus }[]
}

export type ReportSection = {
  n: number
  title: string
  paras?: string[]
  bullets?: string[]
}

export type PlanItem = {
  week: string
  title: string
  body: string
  // Optional priority + forecasted impact promote the plan to a table.
  priority?: 'P0' | 'P1' | 'P2'
  impact?: string
}

// A single point on a weekly trend chart (e.g. blended ROAS over the window).
export type TrendPoint = {
  label: string
  value: number
  display?: string
  tip?: string
  accent?: boolean
}

export type Trend = {
  title: string
  sub: string
  points: TrendPoint[]
}

export type SampleReport = {
  key: string
  industry: string
  brand: string
  fileName: string
  fileSize: string
  meta: string
  healthScore: number
  tldr: string
  alert: { title: string; body: string }
  kpis: KpiTile[]
  comparison: CompRow[]
  breakdown: Breakdown
  // Optional weekly trend (rendered as a vertical-bar chart with an inflection).
  trend?: Trend
  // Optional extra dimension tables — device / geo / wasted-spend search terms /
  // ad-copy — each rendered as its own section with status pills.
  dimensions?: Breakdown[]
  sections: ReportSection[]
  plan: PlanItem[]
  projection: string
}

export const SAMPLE_REPORTS: SampleReport[] = [
  // 1 — Ecommerce / DTC ------------------------------------------------------
  {
    key: 'ecommerce',
    industry: 'Ecommerce',
    brand: 'Northwind Outdoors',
    fileName: 'northwind-ads-audit.html',
    fileSize: '24.3 KB',
    meta: 'Meta Ads + Google Ads + Shopify (read-only) · DTC camping & hiking gear · Last 30 days',
    healthScore: 62,
    tldr: 'Northwind is spending $24,800/mo across Meta and Google at a blended 2.4x ROAS, down from 3.0x last month. The drop is concentrated in Meta: two scaling ad sets fatigued while budget kept flowing to them. We found ~$3,180/mo of recoverable wasted spend and a clear path back to ~2.9x. Health 62/100 — structure and tracking are solid; creative freshness and budget discipline are the weak points.',
    alert: {
      title: '⚠ Budget leak: $3,180/mo to ad sets with zero conversions',
      body: '7 ad sets spent with no conversions in the last 14 days, and $1,940/mo went to branded search terms you already rank #1 for organically. Pausing these is the single highest-leverage move this month.',
    },
    kpis: [
      { label: 'Spend', value: '$24,800', delta: '+14%' },
      { label: 'Revenue', value: '$59,520', delta: '-9%', bad: true },
      { label: 'Blended ROAS', value: '2.4x', delta: '-20%', bad: true },
      { label: 'AOV', value: '$88', delta: '-4%', bad: true },
      { label: 'CAC', value: '$38.10', delta: '+31%', bad: true },
      { label: 'Health', value: '62/100', delta: 'Tracking OK' },
    ],
    comparison: [
      { metric: 'ROAS', current: '2.4x', previous: '3.0x', avg7: '2.6x', avg14: '2.8x', target: '3.0x', status: 'Drawdown' },
      { metric: 'Spend', current: '$24,800', previous: '$21,750', avg7: '$23.1K', avg14: '$22.4K', target: '—', status: 'Normal' },
      { metric: 'Purchases', current: '676', previous: '742', avg7: '702', avg14: '725', target: '780', status: 'Declining' },
      { metric: 'CPA', current: '$38.10', previous: '$29.05', avg7: '$33.4', avg14: '$31.2', target: '$30', status: 'Elevated' },
      { metric: 'CTR', current: '1.9%', previous: '2.4%', avg7: '2.1%', avg14: '2.3%', target: '—', status: 'Declining' },
      { metric: 'Wasted spend', current: '$3,180', previous: '$1,240', avg7: '$2.4K', avg14: '$1.9K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Campaign guardrails',
      columns: ['Campaign', 'Spend', 'ROAS', 'Status'],
      rows: [
        { name: 'Meta · Prospecting LAL', cells: ['$8,420', '1.6x'], status: 'Drawdown' },
        { name: 'Meta · Retargeting', cells: ['$4,110', '4.8x'], status: 'Normal' },
        { name: 'Google · Brand', cells: ['$1,940', '9.1x'], status: 'Flat' },
        { name: 'Google · Shopping', cells: ['$6,150', '2.2x'], status: 'Declining' },
        { name: 'Google · PMax', cells: ['$4,180', '1.9x'], status: 'Elevated' },
      ],
    },
    trend: {
      title: 'Blended ROAS by week',
      sub: '30-day window — the slide is recent and concentrated in Meta prospecting',
      points: [
        { label: 'May 5', value: 305, display: '3.1x' },
        { label: 'May 12', value: 300, display: '3.0x' },
        { label: 'May 19', value: 292, display: '2.9x' },
        { label: 'May 26', value: 278, display: '2.8x' },
        { label: 'Jun 2', value: 262, display: '2.6x' },
        { label: 'Jun 9', value: 250, display: '2.5x' },
        { label: 'Jun 16', value: 240, display: '2.4x', accent: true, tip: '<b>Jun 16</b><br>Blended ROAS 2.4x — below the 2.7x guardrail' },
      ],
    },
    dimensions: [
      {
        title: 'Device performance',
        lede: 'Tablet is pure leak — 10% of spend at 0.6x ROAS with no exclusion set. Mobile, the largest bucket, is where the ROAS slide concentrates.',
        columns: ['Device', 'Spend', 'ROAS', 'Share', 'Status'],
        rows: [
          { name: 'Mobile', cells: ['$14,200', '2.1x', '57%'], status: 'Declining' },
          { name: 'Desktop', cells: ['$8,100', '3.0x', '33%'], status: 'Normal' },
          { name: 'Tablet', cells: ['$2,500', '0.6x', '10%'], status: 'Critical' },
        ],
      },
      {
        title: 'Wasted spend — search terms & placements',
        lede: '≈$3,180/mo flows to queries and placements that don’t convert — broad-match junk, the Audience Network, and brand terms you already own organically.',
        columns: ['Query / placement', 'Spend', 'Conv', 'CPA', 'Status'],
        rows: [
          { name: '“camping gear free”', cells: ['$840', '0', '—'], status: 'Critical' },
          { name: 'Audience Network placement', cells: ['$1,730', '9', '$192'], status: 'Drawdown' },
          { name: '“used tents”', cells: ['$610', '0', '—'], status: 'Critical' },
          { name: 'Brand: “northwind outdoors”', cells: ['$1,940', '61', '$31.8'], status: 'Flat' },
          { name: '“tent repair diy”', cells: ['$430', '0', '—'], status: 'Critical' },
        ],
      },
      {
        title: 'Creative fatigue',
        lede: 'The two highest-spend Meta creatives are deep into fatigue — frequency past 4.5 and CTR down ~40% — while daily budget never moved.',
        columns: ['Creative', 'Spend', 'Freq', 'CTR Δ', 'Status'],
        rows: [
          { name: 'Meta · “Summit” video', cells: ['$3,900', '5.1', '−41%'], status: 'Drawdown' },
          { name: 'Meta · “Trailhead” static', cells: ['$2,600', '4.6', '−33%'], status: 'Declining' },
          { name: 'Meta · “Basecamp” UGC', cells: ['$1,500', '2.1', '+4%'], status: 'Normal' },
        ],
      },
    ],
    sections: [
      {
        n: 2,
        title: 'Wasted spend & budget leaks',
        bullets: [
          '$3,180/mo to 7 ad sets with zero conversions in the last 14 days.',
          '$1,940/mo on branded search terms you already rank #1 for organically.',
          '11% of search budget drained by broad-match terms on irrelevant queries (“free”, “used”, “DIY”).',
        ],
      },
      {
        n: 3,
        title: 'Campaign structure & targeting',
        paras: [
          'Search and Shopping share a single campaign, so smart bidding can’t optimize them independently. Three prospecting ad sets overlap on the same lookalike audience, bidding against each other and inflating CPMs. No device or geo exclusions are set despite zero conversions from tablet traffic.',
        ],
      },
      {
        n: 4,
        title: 'Creative & ad-copy performance',
        paras: [
          'Your 3 highest-spend Meta creatives crossed a frequency of 4.5 three weeks ago. CTR is down 36% and CPM up 18% on that audience — but daily budget never moved, so efficiency fell while spend rose. No new creative has shipped to the top audience in 5 weeks.',
        ],
      },
      {
        n: 5,
        title: 'Conversion tracking gaps',
        paras: [
          'The purchase event fires twice on the checkout confirmation page, inflating reported ROAS by ~0.4x. Enhanced conversions are off, so iOS-attributed revenue is undercounted — the two errors partly mask each other, which is why the numbers “look fine” until you reconcile against Shopify.',
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Stop the bleeding', priority: 'P0', impact: '+~$3,180/mo recovered spend', body: 'Pause the 7 non-converting ad sets and exclude the Audience Network + tablet. Add branded terms as negatives on the generic search campaign.' },
      { week: 'Week 2', title: 'Fix measurement', priority: 'P0', impact: 'True ROAS visibility; removes ~0.4x reporting inflation', body: 'De-dupe the purchase event, enable enhanced conversions, reconcile against Shopify so bidding optimizes on true ROAS.' },
      { week: 'Week 3', title: 'Refresh creative', priority: 'P1', impact: 'Reverses ~40% CTR decay on the top audience', body: 'Ship 3 new concepts to the fatigued audience and cap frequency at 3. Split Search and Shopping into separate campaigns.' },
      { week: 'Week 4', title: 'Reallocate', priority: 'P1', impact: 'Blended ROAS toward ~2.9x at flat spend', body: 'Move ~$4.2k/day from the 2 fatigued ad sets to the 3 efficient ones and consolidate the overlapping lookalikes.' },
    ],
    projection: 'Blended ROAS back to ~2.9x within 30 days at the same spend — recovering ~$3,180/mo of wasted budget and reversing the Meta creative decay.',
  },

  // 2 — Mobile App (subscription) -------------------------------------------
  {
    key: 'app',
    industry: 'Mobile App',
    brand: 'Lumen Sleep',
    fileName: 'lumen-ads-audit.html',
    fileSize: '26.1 KB',
    meta: 'Google Ads (UAC) + Meta Ads + GA4 (read-only) · Sleep & meditation subscription app · Last 30 days',
    healthScore: 58,
    tldr: 'Lumen is spending $38,200/mo driving installs at a $2.40 CPI, but the funnel leaks after install: trial-start rate fell from 9.1% to 6.4% and D7 ROAS sits at 0.42x against a 0.60x target. Most of the spend is chasing cheap installs that never start a trial. We found ~$5,600/mo flowing to two UAC campaigns optimizing for installs instead of in-app events. Health 58/100.',
    alert: {
      title: '⚠ Optimizing for installs, not value — D7 ROAS 0.42x vs 0.60x target',
      body: 'Two UAC campaigns ($5,600/mo) bid on install volume, so Google buys the cheapest, lowest-intent users. Trial-start rate on that traffic is 2.9% vs 9.4% on event-optimized campaigns.',
    },
    kpis: [
      { label: 'Spend', value: '$38,200', delta: '+8%' },
      { label: 'Installs', value: '15,900', delta: '+22%' },
      { label: 'CPI', value: '$2.40', delta: '-11%' },
      { label: 'Trial start %', value: '6.4%', delta: '-30%', bad: true },
      { label: 'D7 ROAS', value: '0.42x', delta: '-18%', bad: true },
      { label: 'Health', value: '58/100', delta: 'Funnel leak' },
    ],
    comparison: [
      { metric: 'D7 ROAS', current: '0.42x', previous: '0.51x', avg7: '0.45x', avg14: '0.48x', target: '0.60x', status: 'Drawdown' },
      { metric: 'CPI', current: '$2.40', previous: '$2.70', avg7: '$2.5', avg14: '$2.6', target: '$2.50', status: 'Improving' },
      { metric: 'Trial starts', current: '1,018', previous: '1,185', avg7: '1,060', avg14: '1,120', target: '1,400', status: 'Declining' },
      { metric: 'Cost / trial', current: '$37.5', previous: '$28.9', avg7: '$33', avg14: '$31', target: '$28', status: 'Elevated' },
      { metric: 'Trial → paid', current: '31%', previous: '34%', avg7: '32%', avg14: '33%', target: '35%', status: 'Declining' },
      { metric: 'Wasted spend', current: '$5,600', previous: '$2,300', avg7: '$4.2K', avg14: '$3.4K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Campaign guardrails',
      columns: ['Campaign', 'Spend', 'D7 ROAS', 'Status'],
      rows: [
        { name: 'UAC · Installs (tCPI)', cells: ['$3,400', '0.18x'], status: 'Drawdown' },
        { name: 'UAC · Installs 2 (tCPI)', cells: ['$2,200', '0.21x'], status: 'Drawdown' },
        { name: 'UAC · In-app (tCPA trial)', cells: ['$12,800', '0.71x'], status: 'Normal' },
        { name: 'Meta · Trial AEO', cells: ['$11,900', '0.63x'], status: 'Normal' },
        { name: 'Meta · Broad prospecting', cells: ['$7,900', '0.39x'], status: 'Elevated' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Funnel leak: install → trial → paid',
        paras: [
          'Install volume is up 22% but trial starts fell 14%, so the install bump is low-quality. The drop is almost entirely the two install-optimized UAC campaigns: their users start a trial at 2.9% vs 9.4% for event-optimized campaigns. You are paying to acquire users the product never activates.',
        ],
      },
      {
        n: 3,
        title: 'Bidding & campaign objective',
        bullets: [
          'Switch the two UAC install campaigns to tCPA on the trial-start event (or fold their budget into the working in-app campaign).',
          'Meta “Broad prospecting” still optimizes for App Install, not trial — move it to AEO/VO on trial-start.',
          'SKAN value mapping only covers install; configure conversion values for trial + paid so iOS can optimize on revenue.',
        ],
      },
      {
        n: 4,
        title: 'Creative & store conversion',
        paras: [
          'Top 2 video creatives are at frequency 5.2 with CTR down 28% — overdue for refresh. Store listing A/B test ended 6 weeks ago with no follow-up; the winning icon variant is not yet live, leaving ~7% install-rate on the table.',
        ],
      },
      {
        n: 5,
        title: 'Measurement',
        paras: [
          'D7 ROAS is computed on a 1-day SKAN window for half the campaigns and 7-day for the rest, so cross-campaign ROAS is not comparable. Standardize on the 7-day postback before reallocating budget.',
        ],
      },
    ],
    trend: {
      title: 'D7 ROAS by week',
      sub: '30-day window — the slide tracks the two install-optimized UAC campaigns scaling up',
      points: [
        { label: 'May 5', value: 53, display: '0.53x' },
        { label: 'May 12', value: 51, display: '0.51x' },
        { label: 'May 19', value: 49, display: '0.49x' },
        { label: 'May 26', value: 47, display: '0.47x' },
        { label: 'Jun 2', value: 45, display: '0.45x' },
        { label: 'Jun 9', value: 43, display: '0.43x' },
        { label: 'Jun 16', value: 42, display: '0.42x', accent: true, tip: '<b>Jun 16</b><br>D7 ROAS 0.42x — 30% below the 0.60x target as install-only UAC spend scaled' },
      ],
    },
    dimensions: [
      {
        title: 'Funnel breakdown — install → trial → paid',
        lede: 'The leak is the activation step: install-optimized traffic starts a trial at 2.9% vs 9.4% on event-optimized campaigns. By the paid step the two cohorts diverge 4x.',
        columns: ['Stage', 'Event-optimized', 'Install-optimized', 'Gap', 'Status'],
        rows: [
          { name: 'Install → trial start', cells: ['9.4%', '2.9%', '−6.5pt'], status: 'Critical' },
          { name: 'Trial → paid', cells: ['33%', '19%', '−14pt'], status: 'Declining' },
          { name: 'Install → paid (blended)', cells: ['3.1%', '0.55%', '−2.55pt'], status: 'Drawdown' },
          { name: 'Cost / paid subscriber', cells: ['$121', '$436', '+$315'], status: 'Critical' },
        ],
      },
      {
        title: 'Geo / market performance',
        lede: 'US + UK carry the working trial economics; the two install-only UAC campaigns dump volume into IN + BR, where trial-start is a third of the rate and cost/trial runs 2–3x the $28 target.',
        columns: ['Market', 'Spend', 'Trial start %', 'Cost / trial', 'Status'],
        rows: [
          { name: 'United States', cells: ['$16,400', '8.7%', '$31'], status: 'Normal' },
          { name: 'United Kingdom', cells: ['$8,100', '7.9%', '$34'], status: 'Normal' },
          { name: 'Canada / AU', cells: ['$6,300', '6.1%', '$41'], status: 'Declining' },
          { name: 'India', cells: ['$4,200', '3.0%', '$79'], status: 'Drawdown' },
          { name: 'Brazil', cells: ['$3,200', '2.6%', '$92'], status: 'Critical' },
        ],
      },
      {
        title: 'Creative fatigue',
        lede: 'The two highest-spend video hooks are past frequency 5 with CTR down ~28%, and no new creative has shipped to the top audience in 6 weeks — refresh is overdue.',
        columns: ['Creative', 'Spend', 'Freq', 'CTR Δ', 'Status'],
        rows: [
          { name: 'Meta · “Wind-down” video', cells: ['$4,300', '5.2', '−28%'], status: 'Drawdown' },
          { name: 'Meta · “Deep sleep” video', cells: ['$3,100', '4.9', '−24%'], status: 'Declining' },
          { name: 'Meta · “Morning energy” UGC', cells: ['$1,800', '2.3', '+6%'], status: 'Normal' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Kill install-only spend', priority: 'P0', impact: '+~$5,600/mo redirected to trial-producing traffic', body: 'Pause both UAC install campaigns; re-deploy $5,600/mo into the in-app (tCPA trial) campaign at +20%/3-day steps.' },
      { week: 'Week 2', title: 'Fix value mapping', priority: 'P0', impact: 'Comparable cross-campaign ROAS; removes 1-day vs 7-day skew', body: 'Configure SKAN conversion values for trial + paid; standardize the 7-day postback window across campaigns.' },
      { week: 'Week 3', title: 'Refresh creative + store', priority: 'P1', impact: 'Reverses ~28% CTR decay; ~7% install-rate lift from the winning icon', body: 'Ship 3 new video hooks to the fatigued audience; push the winning store-listing variant live.' },
      { week: 'Week 4', title: 'Scale what works', priority: 'P1', impact: 'D7 ROAS toward ~0.60x and ~35% more trial starts at flat spend', body: 'Scale Meta Trial AEO and UAC in-app while D7 ROAS holds above 0.6x.' },
    ],
    projection: 'D7 ROAS back to ~0.6x and ~35% more trial starts at the same spend within 30 days.',
  },

  // 3 — Mobile Game ---------------------------------------------------------
  {
    key: 'game',
    industry: 'Mobile Game',
    brand: 'Realmfall RPG',
    fileName: 'realmfall-ads-audit.html',
    fileSize: '25.4 KB',
    meta: 'Google Ads (UAC) + Meta Ads + TikTok Ads (read-only) · Mid-core mobile RPG · Last 30 days',
    healthScore: 54,
    tldr: 'Realmfall spends $61,500/mo on UA at a $1.85 CPI, but ROAS is decaying: D7 ROAS slid from 0.38x to 0.27x as the team scaled spend +34% into a fatiguing creative pool. Whale concentration is high (top 2% of payers = 61% of revenue) yet bidding optimizes on install, not purchase. ~$8,900/mo is going to channels and geos that never produce a payer. Health 54/100.',
    alert: {
      title: '⚠ Scaled spend +34% into a stale creative pool — ROAS decaying',
      body: 'Only 4 creatives carry 78% of spend and all are past frequency 6. New-creative velocity is ~1/week vs the ~5/week needed to hold ROAS at this spend level.',
    },
    kpis: [
      { label: 'Spend', value: '$61,500', delta: '+34%', bad: true },
      { label: 'Installs', value: '33,200', delta: '+29%' },
      { label: 'CPI', value: '$1.85', delta: '+9%', bad: true },
      { label: 'D7 ROAS', value: '0.27x', delta: '-29%', bad: true },
      { label: 'ARPPU', value: '$48', delta: '-6%', bad: true },
      { label: 'Health', value: '54/100', delta: 'Creative-bound' },
    ],
    comparison: [
      { metric: 'D7 ROAS', current: '0.27x', previous: '0.38x', avg7: '0.30x', avg14: '0.33x', target: '0.45x', status: 'Drawdown' },
      { metric: 'CPI', current: '$1.85', previous: '$1.70', avg7: '$1.8', avg14: '$1.75', target: '$1.70', status: 'Elevated' },
      { metric: 'Payer rate', current: '1.4%', previous: '1.9%', avg7: '1.6%', avg14: '1.7%', target: '2.0%', status: 'Declining' },
      { metric: 'Cost / payer', current: '$132', previous: '$89', avg7: '$112', avg14: '$104', target: '$95', status: 'Critical' },
      { metric: 'CTR', current: '0.9%', previous: '1.4%', avg7: '1.1%', avg14: '1.2%', target: '—', status: 'Declining' },
      { metric: 'Wasted spend', current: '$8,900', previous: '$4,100', avg7: '$6.8K', avg14: '$5.5K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Channel & geo guardrails',
      columns: ['Source', 'Spend', 'D7 ROAS', 'Status'],
      rows: [
        { name: 'UAC · Tier-1 geos', cells: ['$22,400', '0.41x'], status: 'Normal' },
        { name: 'UAC · Tier-3 geos', cells: ['$6,300', '0.06x'], status: 'Drawdown' },
        { name: 'Meta · Lookalike payers', cells: ['$16,800', '0.38x'], status: 'Normal' },
        { name: 'TikTok · Broad', cells: ['$10,400', '0.12x'], status: 'Drawdown' },
        { name: 'TikTok · Spark ads', cells: ['$5,600', '0.34x'], status: 'Declining' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Creative fatigue is the binding constraint',
        paras: [
          'You scaled spend 34% on a pool of 4 creatives now all past frequency 6. CTR fell from 1.4% to 0.9% and CPI rose 9%. At this budget you need ~5 fresh concepts/week to hold ROAS; current velocity is ~1/week.',
        ],
      },
      {
        n: 3,
        title: 'Bidding on the wrong event',
        bullets: [
          'UAC Tier-3 geos optimize on install and deliver 0.06x D7 ROAS — turn off or move to a purchase-event bid.',
          'TikTok Broad buys volume with a 0.12x return; pause and reallocate to Spark ads + lookalike-payers.',
          'Switch the two scaling campaigns to tROAS / purchase-value bidding now that you have 60+ payers/week of signal.',
        ],
      },
      {
        n: 4,
        title: 'Monetization signal',
        paras: [
          'Top 2% of payers drive 61% of revenue, but only the purchase event (not purchase value) is sent to the networks, so they cannot find whales. Send value + a high-value-payer event to unlock value-based bidding.',
        ],
      },
    ],
    trend: {
      title: 'D7 ROAS by week',
      sub: '30-day window — ROAS decayed as spend scaled +34% into a 4-creative pool past frequency 6',
      points: [
        { label: 'May 5', value: 39, display: '0.39x' },
        { label: 'May 12', value: 38, display: '0.38x' },
        { label: 'May 19', value: 35, display: '0.35x' },
        { label: 'May 26', value: 33, display: '0.33x' },
        { label: 'Jun 2', value: 31, display: '0.31x' },
        { label: 'Jun 9', value: 29, display: '0.29x' },
        { label: 'Jun 16', value: 27, display: '0.27x', accent: true, tip: '<b>Jun 16</b><br>D7 ROAS 0.27x — 40% below the 0.45x target; CTR collapsed from 1.4% to 0.9% as the 4-creative pool fatigued' },
      ],
    },
    dimensions: [
      {
        title: 'Creative fatigue — the binding constraint',
        lede: 'Only 4 creatives carry 78% of spend and all sit past frequency 6, with CTR down 30–45%. New-creative velocity is ~1/week against the ~5/week needed to hold ROAS at this budget.',
        columns: ['Creative', 'Spend', 'Freq', 'CTR Δ', 'Status'],
        rows: [
          { name: 'TikTok · “Boss fight” hook', cells: ['$15,800', '7.4', '−44%'], status: 'Drawdown' },
          { name: 'Meta · “Loot reveal” video', cells: ['$13,100', '6.9', '−38%'], status: 'Drawdown' },
          { name: 'TikTok · “Class build” hook', cells: ['$9,400', '6.2', '−31%'], status: 'Declining' },
          { name: 'Meta · “Guild war” video', cells: ['$9,700', '6.5', '−35%'], status: 'Declining' },
          { name: 'TikTok · “PvP combo” (fresh)', cells: ['$2,100', '1.8', '+9%'], status: 'Normal' },
        ],
      },
      {
        title: 'Geo / market performance',
        lede: 'Tier-1 geos carry the working payer economics; Tier-3 UAC spends $6,300/mo at 0.06x D7 ROAS — installs that almost never convert to a payer at $640 cost-per-payer.',
        columns: ['Market', 'Spend', 'D7 ROAS', 'Cost / payer', 'Status'],
        rows: [
          { name: 'Tier-1 (US / UK / DE)', cells: ['$22,400', '0.41x', '$98'], status: 'Normal' },
          { name: 'Tier-2 (CA / FR / JP)', cells: ['$18,200', '0.33x', '$121'], status: 'Declining' },
          { name: 'Tier-3 (mixed)', cells: ['$6,300', '0.06x', '$640'], status: 'Drawdown' },
          { name: 'TikTok · Broad (untargeted)', cells: ['$10,400', '0.12x', '$410'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Monetization — payer concentration',
        lede: 'The top 2% of payers drive 61% of revenue, yet only the purchase event (not purchase value) reaches the networks, so they cannot find whales. Whale ARPPU is 30x the minnow tier.',
        columns: ['Payer tier', 'Share of payers', 'Share of revenue', 'ARPPU', 'Status'],
        rows: [
          { name: 'Whales (top 2%)', cells: ['2%', '61%', '$1,460'], status: 'Normal' },
          { name: 'Dolphins (next 18%)', cells: ['18%', '27%', '$72'], status: 'Normal' },
          { name: 'Minnows (bottom 80%)', cells: ['80%', '12%', '$48'], status: 'Flat' },
          { name: 'Value signal sent to networks', cells: ['Event only', 'No value', '—'], status: 'Critical' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Cut the dead weight', priority: 'P0', impact: '+~$8,900/mo recovered from 0.06x–0.12x sources', body: 'Pause Tier-3 UAC + TikTok Broad (recovers ~$8,900/mo).' },
      { week: 'Week 2', title: 'Send value', priority: 'P0', impact: 'Unlocks value-based bidding so networks can find whales', body: 'Instrument purchase value + high-value-payer event; switch scaling campaigns to value-based bidding.' },
      { week: 'Week 3', title: 'Creative engine', priority: 'P1', impact: 'Reverses the CTR slide from 0.9% back toward 1.4%', body: 'Stand up a 5/week concept pipeline; retire all creatives past frequency 5.' },
      { week: 'Week 4', title: 'Rescale carefully', priority: 'P1', impact: 'D7 ROAS toward ~0.42x and cost-per-payer down ~25%', body: 'Rescale Tier-1 + lookalike-payers at +15%/3-day steps while D7 ROAS holds above 0.4x.' },
    ],
    projection: 'D7 ROAS back to ~0.42x and cost-per-payer down ~25% within 30 days.',
  },

  // 4 — Web SaaS (B2B) ------------------------------------------------------
  {
    key: 'saas',
    industry: 'Web SaaS',
    brand: 'Flowdesk',
    fileName: 'flowdesk-ads-audit.html',
    fileSize: '23.8 KB',
    meta: 'Google Ads (Search) + LinkedIn + Meta (read-only) · B2B workflow SaaS · Last 30 days',
    healthScore: 67,
    tldr: 'Flowdesk spends $19,400/mo, mostly on Google Search, at a $96 cost-per-signup. The account is structurally healthy but optimizes on signups, not qualified pipeline — and 38% of signups are free-mail / student traffic that never convert to a paid seat. Aligning bidding to MQL/opportunity would cut cost-per-opportunity by an estimated 30%. Health 67/100.',
    alert: {
      title: '⚠ Bidding on signups, not pipeline — 38% of signups are unqualified',
      body: 'Search optimizes on the free-trial signup event. 38% of those signups are free-mail/student, so smart bidding is steering budget toward cheap, low-intent registrations instead of revenue.',
    },
    kpis: [
      { label: 'Spend', value: '$19,400', delta: '+6%' },
      { label: 'Signups', value: '202', delta: '+11%' },
      { label: 'Cost / signup', value: '$96', delta: '-4%' },
      { label: 'Cost / MQL', value: '$312', delta: '+22%', bad: true },
      { label: 'Pipeline', value: '$184K', delta: '-8%', bad: true },
      { label: 'Health', value: '67/100', delta: 'Structure OK' },
    ],
    comparison: [
      { metric: 'Cost / MQL', current: '$312', previous: '$256', avg7: '$290', avg14: '$274', target: '$240', status: 'Elevated' },
      { metric: 'Signups', current: '202', previous: '182', avg7: '195', avg14: '188', target: '210', status: 'Normal' },
      { metric: 'MQL rate', current: '31%', previous: '38%', avg7: '34%', avg14: '36%', target: '40%', status: 'Declining' },
      { metric: 'Cost / opp', current: '$1,180', previous: '$940', avg7: '$1.1K', avg14: '$1.0K', target: '$900', status: 'Critical' },
      { metric: 'Search IS (lost-budget)', current: '18%', previous: '9%', avg7: '14%', avg14: '12%', target: '<5%', status: 'Elevated' },
      { metric: 'Wasted spend', current: '$2,400', previous: '$1,500', avg7: '$2.0K', avg14: '$1.8K', target: '$0', status: 'Declining' },
    ],
    breakdown: {
      title: 'Campaign guardrails',
      columns: ['Campaign', 'Spend', 'Cost / MQL', 'Status'],
      rows: [
        { name: 'Search · Brand', cells: ['$2,100', '$84'], status: 'Flat' },
        { name: 'Search · Category terms', cells: ['$7,800', '$268'], status: 'Normal' },
        { name: 'Search · Competitor', cells: ['$3,600', '$540'], status: 'Elevated' },
        { name: 'LinkedIn · ABM', cells: ['$4,200', '$361'], status: 'Normal' },
        { name: 'Meta · Retargeting', cells: ['$1,700', '$190'], status: 'Improving' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Lead quality, not lead volume',
        paras: [
          '38% of trial signups use free-mail or student domains and convert to a paid seat at 1/6 the rate of business signups. Because Search bids on the raw signup event, smart bidding actively favors this cheap traffic — cost-per-signup looks fine while cost-per-MQL is up 22%.',
        ],
      },
      {
        n: 3,
        title: 'Offline-conversion import',
        bullets: [
          'Import MQL and opportunity stages from the CRM as offline conversions, then bid on MQL value — not signup.',
          'Exclude free-mail/student domains at the form and as audience signals.',
          'Competitor campaign cost-per-MQL is $540 (2x category) with no clear pipeline — cap or pause.',
        ],
      },
      {
        n: 4,
        title: 'Budget & impression share',
        paras: [
          'Brand + category terms are losing 18% of impression share to budget despite the strongest pipeline economics. Shift budget here from the competitor campaign and the underperforming broad keywords.',
        ],
      },
    ],
    trend: {
      title: 'Cost per MQL by week',
      sub: '30-day window — climbing as smart bidding chases cheap, unqualified signups',
      points: [
        { label: 'May 5', value: 248, display: '$248' },
        { label: 'May 12', value: 256, display: '$256' },
        { label: 'May 19', value: 270, display: '$270' },
        { label: 'May 26', value: 284, display: '$284' },
        { label: 'Jun 2', value: 296, display: '$296' },
        { label: 'Jun 9', value: 305, display: '$305' },
        { label: 'Jun 16', value: 312, display: '$312', accent: true, tip: '<b>Jun 16</b><br>Cost/MQL $312 — 30% over the $240 target; MQL rate fell to 31% as free-mail signups rose' },
      ],
    },
    dimensions: [
      {
        title: 'Lead quality by signup domain',
        lede: '38% of trial signups are free-mail or student domains, and they reach MQL at roughly 1/6 the business rate. Because Search bids on the raw signup, smart bidding actively favors this cheap, low-intent traffic.',
        columns: ['Signup source', 'Share', 'Cost / signup', 'MQL rate', 'Status'],
        rows: [
          { name: 'Business domain', cells: ['62%', '$118', '44%'], status: 'Normal' },
          { name: 'Free-mail (gmail / outlook)', cells: ['29%', '$61', '8%'], status: 'Drawdown' },
          { name: 'Student / .edu', cells: ['9%', '$52', '5%'], status: 'Critical' },
        ],
      },
      {
        title: 'Wasted spend — search terms',
        lede: '≈$2,400/mo flows to research/job-seeker queries and an over-bid competitor campaign that produce signups but no qualified pipeline.',
        columns: ['Query / campaign', 'Spend', 'Signups', 'Cost / MQL', 'Status'],
        rows: [
          { name: '“free workflow tool”', cells: ['$680', '14', '$510'], status: 'Drawdown' },
          { name: '“flowdesk jobs”', cells: ['$240', '0', '—'], status: 'Critical' },
          { name: '“workflow software student”', cells: ['$310', '9', '$620'], status: 'Critical' },
          { name: 'Competitor campaign', cells: ['$3,600', '67', '$540'], status: 'Elevated' },
          { name: '“workflow tutorial”', cells: ['$290', '6', '$480'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Device performance',
        lede: 'Mobile drives a quarter of signups but converts to MQL at less than half the desktop rate — B2B intent concentrates on desktop, yet no device bid adjustment is set.',
        columns: ['Device', 'Spend', 'Signups', 'MQL rate', 'Status'],
        rows: [
          { name: 'Desktop', cells: ['$13,600', '139', '39%'], status: 'Normal' },
          { name: 'Mobile', cells: ['$4,900', '52', '16%'], status: 'Declining' },
          { name: 'Tablet', cells: ['$900', '11', '11%'], status: 'Drawdown' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Connect the CRM', priority: 'P0', impact: 'Enables bidding on MQL value instead of raw signups', body: 'Set up offline-conversion import for MQL + opportunity; start logging values.' },
      { week: 'Week 2', title: 'Filter junk', priority: 'P0', impact: '+~$2,400/mo recovered from free-mail + competitor waste', body: 'Add free-mail/student exclusions; cap the $540-cost/MQL competitor campaign.' },
      { week: 'Week 3', title: 'Re-target bidding', priority: 'P1', impact: 'Cost-per-opportunity down ~30% as bidding learns on MQL value', body: 'Switch eligible Search campaigns to bid on MQL value once 30+ conversions/week are flowing.' },
      { week: 'Week 4', title: 'Fund the winners', priority: 'P1', impact: 'Recovers 18% lost impression share on the highest-pipeline terms', body: 'Move freed budget into brand + category terms to recover lost impression share.' },
    ],
    projection: 'Cost-per-opportunity down ~30% and qualified pipeline up ~$60K within 60 days as bidding learns on MQL value.',
  },

  // 5 — Fintech app ---------------------------------------------------------
  {
    key: 'fintech',
    industry: 'Fintech',
    brand: 'Cointide',
    fileName: 'cointide-ads-audit.html',
    fileSize: '24.7 KB',
    meta: 'Google Ads (UAC) + Meta Ads + Apple Search Ads (read-only) · Investing & savings app · Last 30 days',
    healthScore: 49,
    tldr: 'Cointide spends $44,300/mo acquiring app installs at $3.10 CPI, but the regulated funnel leaks hard: only 22% of installs complete KYC and only 41% of those fund an account. Cost-per-funded-account hit $214 against a $150 target. Bidding optimizes on registration, not the funded event, so the networks buy users who never fund. Health 49/100.',
    alert: {
      title: '⚠ Cost-per-funded-account $214 vs $150 target — optimizing pre-KYC',
      body: 'Campaigns bid on the registration event, which sits two steps before revenue. 78% of registered users never complete KYC, so a third of spend buys accounts that can never fund.',
    },
    kpis: [
      { label: 'Spend', value: '$44,300', delta: '+12%' },
      { label: 'Installs', value: '14,300', delta: '+18%' },
      { label: 'KYC complete %', value: '22%', delta: '-15%', bad: true },
      { label: 'Funded %', value: '41%', delta: '-9%', bad: true },
      { label: 'Cost / funded', value: '$214', delta: '+27%', bad: true },
      { label: 'Health', value: '49/100', delta: 'Funnel leak' },
    ],
    comparison: [
      { metric: 'Cost / funded', current: '$214', previous: '$168', avg7: '$198', avg14: '$184', target: '$150', status: 'Drawdown' },
      { metric: 'Installs', current: '14,300', previous: '12,100', avg7: '13.4K', avg14: '12.9K', target: '—', status: 'Normal' },
      { metric: 'KYC rate', current: '22%', previous: '26%', avg7: '24%', avg14: '25%', target: '30%', status: 'Declining' },
      { metric: 'Funded accounts', current: '207', previous: '264', avg7: '232', avg14: '248', target: '300', status: 'Declining' },
      { metric: 'Avg first deposit', current: '$180', previous: '$205', avg7: '$192', avg14: '$198', target: '$220', status: 'Declining' },
      { metric: 'Wasted spend', current: '$9,800', previous: '$5,200', avg7: '$7.9K', avg14: '$6.6K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Channel guardrails',
      columns: ['Source', 'Spend', 'Cost / funded', 'Status'],
      rows: [
        { name: 'ASA · Brand', cells: ['$4,600', '$92'], status: 'Flat' },
        { name: 'UAC · Registration', cells: ['$14,200', '$286'], status: 'Drawdown' },
        { name: 'Meta · Registration AEO', cells: ['$13,800', '$241'], status: 'Elevated' },
        { name: 'Meta · Funded-event VO', cells: ['$7,900', '$148'], status: 'Normal' },
        { name: 'UAC · Tier-3 geos', cells: ['$3,800', '$520'], status: 'Drawdown' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Optimize on the funded event',
        paras: [
          'The Meta campaign that already bids on the funded deposit event runs at $148 cost-per-funded — at target — while registration-optimized campaigns run 60–90% higher. The fix is to move budget to value-based bidding on first-deposit value, not to acquire more registrations.',
        ],
      },
      {
        n: 3,
        title: 'KYC drop-off',
        bullets: [
          'KYC completion fell from 26% to 22%; the document-upload step is the largest single drop — worth a product fix, not just media.',
          'Tier-3 UAC geos fund at $520 each — pause and concentrate on Tier-1.',
          'Suppress already-funded users from prospecting (currently 6% of impressions).',
        ],
      },
      {
        n: 4,
        title: 'Compliance & creative',
        paras: [
          'Two creatives imply guaranteed returns and risk policy disapproval; replace before they trigger an account-level flag. Required risk disclosure is missing from 3 active ads.',
        ],
      },
    ],
    trend: {
      title: 'Cost-per-funded-account by week',
      sub: '30-day window — the climb tracks +18% install scaling poured into registration-optimized campaigns',
      points: [
        { label: 'May 5', value: 166, display: '$166' },
        { label: 'May 12', value: 172, display: '$172' },
        { label: 'May 19', value: 181, display: '$181' },
        { label: 'May 26', value: 190, display: '$190' },
        { label: 'Jun 2', value: 199, display: '$199' },
        { label: 'Jun 9', value: 207, display: '$207' },
        { label: 'Jun 16', value: 214, display: '$214', accent: true, tip: '<b>Jun 16</b><br>Cost-per-funded $214 — 43% above the $150 target' },
      ],
    },
    dimensions: [
      {
        title: 'Funnel / event breakdown',
        lede: 'Install → KYC → funded leaks at every gate: only 22% clear KYC and 41% of those fund, so 91% of acquired installs never reach revenue.',
        columns: ['Stage', 'Count', 'Step rate', 'Cost / step', 'Status'],
        rows: [
          { name: 'Installs', cells: ['14,300', '—', '$3.10'], status: 'Normal' },
          { name: 'Registrations', cells: ['8,294', '58%', '$5.34'], status: 'Normal' },
          { name: 'KYC complete', cells: ['3,146', '22%', '$14.08'], status: 'Declining' },
          { name: 'Funded accounts', cells: ['207', '41%', '$214'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Geo / market performance',
        lede: 'Tier-3 geos fund at $520 each — 3.5x the Tier-1 rate — yet still draw $3,800/mo with no geo guardrail. Tier-1 is the only segment near target.',
        columns: ['Market', 'Spend', 'Funded', 'Cost / funded', 'Status'],
        rows: [
          { name: 'Tier-1 (US/CA/UK)', cells: ['$24,100', '142', '$170'], status: 'Normal' },
          { name: 'Tier-2 (EU/AU)', cells: ['$16,400', '54', '$304'], status: 'Elevated' },
          { name: 'Tier-3 (rest)', cells: ['$3,800', '7', '$520'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Compliance / ad-copy risk',
        lede: 'Three active ads carry policy risk — implied-return language and missing risk disclosure put the whole account one flag away from a suspension.',
        columns: ['Creative', 'Spend', 'Issue', 'Status'],
        rows: [
          { name: 'Meta · “Grow 2x faster” static', cells: ['$2,400', 'Implied guaranteed return'], status: 'Critical' },
          { name: 'Meta · “Beat the market” video', cells: ['$1,900', 'Implied guaranteed return'], status: 'Critical' },
          { name: 'UAC · “Start investing” bundle', cells: ['$1,200', 'Missing risk disclosure'], status: 'Elevated' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Shift to funded-event bidding', priority: 'P0', impact: '+~$9,800/mo recovered spend', body: 'Move budget from registration campaigns to the funded-event VO campaign; pause Tier-3 geos.' },
      { week: 'Week 2', title: 'Pass deposit value', priority: 'P0', impact: 'Unlocks value-based bidding on first-deposit revenue', body: 'Send first-deposit value to all networks; enable value-based bidding.' },
      { week: 'Week 3', title: 'Patch KYC + compliance', priority: 'P1', impact: 'Lifts KYC rate back toward 26%; clears suspension risk', body: 'Fix the document-upload drop-off; replace the non-compliant creatives.' },
      { week: 'Week 4', title: 'Scale at target', priority: 'P2', impact: 'Cost-per-funded toward ~$155; +~30% funded accounts', body: 'Scale the funded-event campaign while cost-per-funded holds under $160.' },
    ],
    projection: 'Cost-per-funded-account back toward ~$155 and ~30% more funded accounts at the same spend within 45 days.',
  },

  // 6 — Marketplace ---------------------------------------------------------
  {
    key: 'marketplace',
    industry: 'Marketplace',
    brand: 'Knack',
    fileName: 'knack-ads-audit.html',
    fileSize: '23.5 KB',
    meta: 'Google Ads (Search + PMax) + Meta (read-only) · Home-services marketplace · Last 30 days',
    healthScore: 60,
    tldr: 'Knack spends $28,600/mo acquiring demand (bookings) but a two-sided marketplace problem is hiding in the data: 23% of paid clicks land in metros with too few providers, so they bounce without booking. Cost-per-booking rose to $41 against a $32 target. Concentrating spend in liquid metros and bidding on completed bookings (not leads) is the unlock. Health 60/100.',
    alert: {
      title: '⚠ $6,580/mo into low-liquidity metros that can’t fulfill demand',
      body: '23% of spend targets metros where provider supply is too thin; those clicks convert to a completed booking at 1/4 the rate of liquid metros.',
    },
    kpis: [
      { label: 'Spend', value: '$28,600', delta: '+10%' },
      { label: 'Bookings', value: '698', delta: '-7%', bad: true },
      { label: 'Cost / booking', value: '$41', delta: '+24%', bad: true },
      { label: 'Take rate', value: '14%', delta: '0%' },
      { label: 'Booking value', value: '$143', delta: '-3%', bad: true },
      { label: 'Health', value: '60/100', delta: 'Liquidity gap' },
    ],
    comparison: [
      { metric: 'Cost / booking', current: '$41', previous: '$33', avg7: '$38', avg14: '$36', target: '$32', status: 'Drawdown' },
      { metric: 'Bookings', current: '698', previous: '751', avg7: '720', avg14: '735', target: '800', status: 'Declining' },
      { metric: 'Click → booking', current: '3.1%', previous: '3.9%', avg7: '3.4%', avg14: '3.6%', target: '4.2%', status: 'Declining' },
      { metric: 'Repeat-booker %', current: '28%', previous: '31%', avg7: '29%', avg14: '30%', target: '35%', status: 'Declining' },
      { metric: 'PMax share', current: '52%', previous: '40%', avg7: '47%', avg14: '44%', target: '—', status: 'Elevated' },
      { metric: 'Wasted spend', current: '$6,580', previous: '$3,900', avg7: '$5.4K', avg14: '$4.7K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Geo & campaign guardrails',
      columns: ['Segment', 'Spend', 'Cost / booking', 'Status'],
      rows: [
        { name: 'Search · Liquid metros', cells: ['$11,200', '$28'], status: 'Normal' },
        { name: 'Search · Thin metros', cells: ['$4,300', '$96'], status: 'Drawdown' },
        { name: 'PMax · All geos', cells: ['$9,400', '$47'], status: 'Elevated' },
        { name: 'Meta · Retargeting', cells: ['$2,400', '$22'], status: 'Improving' },
        { name: 'Search · Brand', cells: ['$1,300', '$11'], status: 'Flat' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Liquidity-aware geo targeting',
        paras: [
          'Cost-per-booking is $28 in liquid metros and $96 in thin ones — same product, 3.4x difference driven by supply, not demand. PMax has no geo guardrails and is quietly expanding spend into thin metros (+12pt share). Cap PMax geos to liquid metros until supply catches up.',
        ],
      },
      {
        n: 3,
        title: 'Bid on completed bookings',
        bullets: [
          'Half the spend bids on the lead/request event, not the completed booking — so cancellations and unfulfilled requests count as conversions.',
          'Feed completed-booking value back to Google; switch to value-based bidding.',
          'Exclude already-active customers from prospecting and route them to a repeat-booking campaign.',
        ],
      },
      {
        n: 4,
        title: 'Retargeting is underfunded',
        paras: [
          'Retargeting books at $22 (best in the account) on just 8% of budget. There is clear room to scale it for repeat bookings, which are down 3pt.',
        ],
      },
    ],
    trend: {
      title: 'Cost-per-booking by week',
      sub: '30-day window — the climb tracks PMax share rising from 40% to 52% as it expanded into thin metros',
      points: [
        { label: 'May 5', value: 32, display: '$32' },
        { label: 'May 12', value: 34, display: '$34' },
        { label: 'May 19', value: 36, display: '$36' },
        { label: 'May 26', value: 37, display: '$37' },
        { label: 'Jun 2', value: 39, display: '$39' },
        { label: 'Jun 9', value: 40, display: '$40' },
        { label: 'Jun 16', value: 41, display: '$41', accent: true, tip: '<b>Jun 16</b><br>Cost-per-booking $41 — 28% above the $32 target' },
      ],
    },
    dimensions: [
      {
        title: 'Geo / market liquidity',
        lede: 'Same product, 3.4x cost gap driven entirely by provider supply: liquid metros book at $28, thin metros at $96. PMax is quietly tilting the mix toward thin.',
        columns: ['Metro tier', 'Spend', 'Click → booking', 'Cost / booking', 'Status'],
        rows: [
          { name: 'Liquid metros', cells: ['$15,400', '4.6%', '$28'], status: 'Normal' },
          { name: 'Mixed-supply metros', cells: ['$6,580', '2.4%', '$58'], status: 'Elevated' },
          { name: 'Thin metros', cells: ['$6,620', '1.1%', '$96'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Funnel / event breakdown',
        lede: 'Half of spend bids on the lead/request event, so unfulfilled requests and cancellations count as conversions — completed bookings are 31% below requests.',
        columns: ['Stage', 'Count', 'Step rate', 'Status'],
        rows: [
          { name: 'Service requests', cells: ['1,012', '—'], status: 'Normal' },
          { name: 'Provider matches', cells: ['814', '80%'], status: 'Normal' },
          { name: 'Completed bookings', cells: ['698', '86%'], status: 'Declining' },
          { name: 'Repeat bookings', cells: ['195', '28%'], status: 'Declining' },
        ],
      },
      {
        title: 'Wasted spend — search terms & placements',
        lede: '≈$6,580/mo flows to thin-metro queries and broad PMax placements that bounce without a fulfilled booking.',
        columns: ['Query / placement', 'Spend', 'Bookings', 'Cost / booking', 'Status'],
        rows: [
          { name: '“handyman near me” (thin metros)', cells: ['$2,340', '24', '$98'], status: 'Drawdown' },
          { name: 'PMax · Display expansion', cells: ['$1,910', '17', '$112'], status: 'Drawdown' },
          { name: '“free quote cleaning”', cells: ['$880', '0', '—'], status: 'Critical' },
          { name: '“diy plumbing fix”', cells: ['$620', '0', '—'], status: 'Critical' },
          { name: 'PMax · Search partners', cells: ['$830', '9', '$92'], status: 'Drawdown' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Geo-cap PMax + Search', priority: 'P0', impact: '+~$6,580/mo recovered spend', body: 'Restrict spend to liquid metros; resume thin metros only as supply fills.' },
      { week: 'Week 2', title: 'Fix the conversion', priority: 'P0', impact: 'Stops counting unfulfilled requests; true cost-per-booking', body: 'Switch bidding to completed-booking value; stop counting lead requests.' },
      { week: 'Week 3', title: 'Scale retargeting', priority: 'P1', impact: 'Lifts repeat-booker rate; books incremental demand at ~$22', body: 'Double retargeting budget; launch a repeat-booking campaign for active customers.' },
      { week: 'Week 4', title: 'Reopen supply-ready metros', priority: 'P2', impact: 'Cost-per-booking toward ~$33; +~12% bookings', body: 'Reintroduce thin metros that crossed the supply threshold this month.' },
    ],
    projection: 'Cost-per-booking back to ~$33 and ~12% more bookings at the same spend within 30 days.',
  },

  // 7 — Health & wellness DTC -----------------------------------------------
  {
    key: 'wellness',
    industry: 'Health & Wellness',
    brand: 'Verve Supplements',
    fileName: 'verve-ads-audit.html',
    fileSize: '24.0 KB',
    meta: 'Meta Ads + Google Ads + Shopify (read-only) · Subscription supplements DTC · Last 30 days',
    healthScore: 64,
    tldr: 'Verve spends $33,100/mo at a blended 2.7x ROAS, but the subscription model is mis-measured: ads optimize on first-order value, ignoring the ~3.1x 90-day LTV from subscriptions, so smart bidding underpays for the best subscribers. Meanwhile a “free shipping” promo audience is cannibalizing organic repeat buyers. Health 64/100.',
    alert: {
      title: '⚠ Bidding on first-order value — subscription LTV invisible to the algorithm',
      body: 'Only the initial purchase value is sent to the networks. Subscribers are worth ~3.1x more at 90 days, but bidding can’t see it, so it underspends on subscription-intent audiences.',
    },
    kpis: [
      { label: 'Spend', value: '$33,100', delta: '+9%' },
      { label: 'Revenue (D1)', value: '$89,370', delta: '-5%', bad: true },
      { label: 'Blended ROAS', value: '2.7x', delta: '-11%', bad: true },
      { label: 'Sub rate', value: '34%', delta: '-6%', bad: true },
      { label: 'CAC', value: '$41', delta: '+14%', bad: true },
      { label: 'Health', value: '64/100', delta: 'LTV blind' },
    ],
    comparison: [
      { metric: 'ROAS (D1)', current: '2.7x', previous: '3.0x', avg7: '2.8x', avg14: '2.9x', target: '3.0x', status: 'Declining' },
      { metric: 'Subscriptions', current: '512', previous: '560', avg7: '530', avg14: '545', target: '600', status: 'Declining' },
      { metric: 'Sub CAC', current: '$64', previous: '$52', avg7: '$59', avg14: '$56', target: '$50', status: 'Elevated' },
      { metric: 'CTR', current: '1.6%', previous: '2.0%', avg7: '1.8%', avg14: '1.9%', target: '—', status: 'Declining' },
      { metric: '90-day LTV / CAC', current: '3.1', previous: '3.4', avg7: '3.2', avg14: '3.3', target: '3.5', status: 'Normal' },
      { metric: 'Wasted spend', current: '$3,900', previous: '$2,100', avg7: '$3.2K', avg14: '$2.7K', target: '$0', status: 'Elevated' },
    ],
    breakdown: {
      title: 'Campaign guardrails',
      columns: ['Campaign', 'Spend', 'ROAS', 'Status'],
      rows: [
        { name: 'Meta · Subscription LAL', cells: ['$10,900', '3.4x'], status: 'Normal' },
        { name: 'Meta · Free-shipping promo', cells: ['$6,700', '1.9x'], status: 'Drawdown' },
        { name: 'Google · Brand', cells: ['$2,300', '8.2x'], status: 'Flat' },
        { name: 'Google · PMax', cells: ['$8,400', '2.5x'], status: 'Declining' },
        { name: 'Meta · Retargeting', cells: ['$4,800', '4.6x'], status: 'Normal' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Make subscription LTV visible',
        paras: [
          'The subscription LAL campaign already returns the best ROAS (3.4x on first order) and far better at 90 days, yet it gets only a third of prospecting budget because bidding optimizes on first-order value. Send a subscription-start event with predicted value so the algorithm can pay for the right customer.',
        ],
      },
      {
        n: 3,
        title: 'Promo cannibalization',
        bullets: [
          'The free-shipping promo audience overlaps 41% with organic repeat buyers — you’re paying to discount sales you’d get free.',
          'Promo campaign ROAS is 1.9x and its subscribers churn 1.8x faster; cap it and exclude recent organic purchasers.',
          'PMax has no audience signal for subscription intent; add one.',
        ],
      },
      {
        n: 4,
        title: 'Creative & landing page',
        paras: [
          'Top creatives are at frequency 4.8; subscription benefit is buried below the fold on the landing page, suppressing sub-rate by an estimated 4–6pt.',
        ],
      },
    ],
    trend: {
      title: 'Blended D1 ROAS by week',
      sub: '30-day window — the slide tracks the free-shipping promo scaling into organic-buyer overlap',
      points: [
        { label: 'May 5', value: 300, display: '3.0x' },
        { label: 'May 12', value: 296, display: '3.0x' },
        { label: 'May 19', value: 290, display: '2.9x' },
        { label: 'May 26', value: 283, display: '2.8x' },
        { label: 'Jun 2', value: 278, display: '2.8x' },
        { label: 'Jun 9', value: 272, display: '2.7x' },
        { label: 'Jun 16', value: 270, display: '2.7x', accent: true, tip: '<b>Jun 16</b><br>Blended D1 ROAS 2.7x — below the 3.0x target, and it ignores ~3.1x 90-day LTV' },
      ],
    },
    dimensions: [
      {
        title: 'Audience overlap',
        lede: 'The free-shipping promo audience overlaps 41% with organic repeat buyers — you are paying to discount sales you would have closed for free, and those subscribers churn 1.8x faster.',
        columns: ['Audience', 'Spend', 'Overlap w/ organic', 'ROAS', 'Status'],
        rows: [
          { name: 'Free-shipping promo', cells: ['$6,700', '41%', '1.9x'], status: 'Drawdown' },
          { name: 'Subscription LAL', cells: ['$10,900', '9%', '3.4x'], status: 'Normal' },
          { name: 'Retargeting', cells: ['$4,800', '33%', '4.6x'], status: 'Normal' },
        ],
      },
      {
        title: 'LTV by cohort — D1 vs 90-day',
        lede: 'Bidding sees only first-order value, but subscribers compound to ~3.1x by day 90. The algorithm underpays for exactly the customers worth the most.',
        columns: ['Cohort', 'D1 value', '90-day value', 'LTV multiple', 'Status'],
        rows: [
          { name: 'Subscribers', cells: ['$58', '$181', '3.1x'], status: 'Improving' },
          { name: 'One-time buyers', cells: ['$72', '$88', '1.2x'], status: 'Normal' },
          { name: 'Promo one-time buyers', cells: ['$49', '$54', '1.1x'], status: 'Declining' },
        ],
      },
      {
        title: 'Creative fatigue',
        lede: 'The top two Meta creatives crossed frequency 4.8 with CTR off ~30%, and none lead with the subscription offer — buried below the fold, it is suppressing sub-rate 4–6pt.',
        columns: ['Creative', 'Spend', 'Freq', 'CTR Δ', 'Status'],
        rows: [
          { name: 'Meta · “Daily Ritual” video', cells: ['$3,600', '4.9', '−31%'], status: 'Drawdown' },
          { name: 'Meta · “Clinically dosed” static', cells: ['$2,400', '4.7', '−24%'], status: 'Declining' },
          { name: 'Meta · “Subscribe & save” UGC', cells: ['$1,300', '2.3', '+6%'], status: 'Normal' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Cap promo cannibalization', priority: 'P0', impact: '+~$3,900/mo recovered spend', body: 'Cap the free-shipping campaign and exclude recent organic buyers.' },
      { week: 'Week 2', title: 'Send subscription value', priority: 'P0', impact: 'Exposes ~3.1x LTV to bidding for the first time', body: 'Instrument a subscription-start event with predicted LTV; feed it to all networks.' },
      { week: 'Week 3', title: 'Re-bid on LTV', priority: 'P1', impact: 'Reallocates toward 3.4x subscription LAL; +~15% subs', body: 'Switch the subscription LAL + PMax to value-based bidding on subscription value.' },
      { week: 'Week 4', title: 'Refresh + scale', priority: 'P1', impact: 'Blended D1 ROAS toward ~3.0x; lifts sub-rate 4–6pt', body: 'Ship new creative leading with the subscription offer; scale the LAL campaign.' },
    ],
    projection: 'Blended D1 ROAS back to ~3.0x and ~15% more subscriptions at the same spend within 30 days.',
  },

  // 8 — EdTech --------------------------------------------------------------
  {
    key: 'edtech',
    industry: 'EdTech',
    brand: 'Lingua',
    fileName: 'lingua-ads-audit.html',
    fileSize: '23.6 KB',
    meta: 'Google Ads (UAC) + Meta + TikTok (read-only) · Language-learning app · Last 30 days',
    healthScore: 61,
    tldr: 'Lingua spends $27,800/mo driving free signups at a $4.20 cost-per-signup, but seasonality and a weak trial-to-paid step are dragging efficiency: free-to-paid fell from 7.2% to 5.4%. Spend is evenly split across geos with wildly different monetization, and the highest-paying geo is budget-capped. Health 61/100.',
    alert: {
      title: '⚠ Top-monetizing geo is budget-capped while flat geos run free',
      body: 'Your best free-to-paid geo (8.9%) loses 21% impression share to budget, while three low-converting geos (2–3%) run uncapped — a direct misallocation.',
    },
    kpis: [
      { label: 'Spend', value: '$27,800', delta: '+7%' },
      { label: 'Signups', value: '6,620', delta: '+14%' },
      { label: 'Cost / signup', value: '$4.20', delta: '-6%' },
      { label: 'Free → paid', value: '5.4%', delta: '-25%', bad: true },
      { label: 'Cost / paid', value: '$78', delta: '+24%', bad: true },
      { label: 'Health', value: '61/100', delta: 'Geo mix' },
    ],
    comparison: [
      { metric: 'Free → paid', current: '5.4%', previous: '7.2%', avg7: '6.0%', avg14: '6.4%', target: '7.5%', status: 'Drawdown' },
      { metric: 'Signups', current: '6,620', previous: '5,810', avg7: '6.3K', avg14: '6.1K', target: '—', status: 'Normal' },
      { metric: 'Paid subs', current: '357', previous: '418', avg7: '380', avg14: '400', target: '480', status: 'Declining' },
      { metric: 'Cost / paid', current: '$78', previous: '$63', avg7: '$72', avg14: '$68', target: '$60', status: 'Elevated' },
      { metric: 'CTR', current: '2.2%', previous: '2.6%', avg7: '2.4%', avg14: '2.5%', target: '—', status: 'Declining' },
      { metric: 'Wasted spend', current: '$4,300', previous: '$2,400', avg7: '$3.5K', avg14: '$3.0K', target: '$0', status: 'Elevated' },
    ],
    breakdown: {
      title: 'Geo guardrails',
      columns: ['Geo', 'Spend', 'Free → paid', 'Status'],
      rows: [
        { name: 'Geo A (budget-capped)', cells: ['$6,100', '8.9%'], status: 'Flat' },
        { name: 'Geo B', cells: ['$7,400', '5.8%'], status: 'Normal' },
        { name: 'Geo C', cells: ['$5,900', '2.6%'], status: 'Drawdown' },
        { name: 'Geo D', cells: ['$4,800', '2.9%'], status: 'Drawdown' },
        { name: 'Retargeting (all)', cells: ['$3,600', '11.4%'], status: 'Improving' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Reallocate by geo monetization',
        paras: [
          'Cost-per-signup is roughly flat across geos, but free-to-paid ranges from 2.6% to 8.9%. You are funding the weak geos uncapped while the strongest is losing 21% impression share to budget. Reallocate toward Geo A and retargeting.',
        ],
      },
      {
        n: 3,
        title: 'Optimize on trial/paid, not signup',
        bullets: [
          'Campaigns bid on free signup; move to the start-trial or paid event so the networks learn who actually converts.',
          'Retargeting converts at 11.4% on 13% of budget — clearly underfunded.',
          'Seasonal back-to-school creative is still live 6 weeks past peak.',
        ],
      },
      {
        n: 4,
        title: 'Trial-to-paid step',
        paras: [
          'Free-to-paid dropped 25%, partly product (paywall timing) and partly media (low-intent geos). Re-sequence the onboarding paywall and re-test the annual offer.',
        ],
      },
    ],
    trend: {
      title: 'Free → paid by week',
      sub: '30-day window — the slide tracks back-to-school creative going stale and budget stuck in low-yield geos',
      points: [
        { label: 'May 5', value: 72, display: '7.2%' },
        { label: 'May 12', value: 69, display: '6.9%' },
        { label: 'May 19', value: 66, display: '6.6%' },
        { label: 'May 26', value: 62, display: '6.2%' },
        { label: 'Jun 2', value: 59, display: '5.9%' },
        { label: 'Jun 9', value: 56, display: '5.6%' },
        { label: 'Jun 16', value: 54, display: '5.4%', accent: true, tip: '<b>Jun 16</b><br>Free → paid 5.4% — 25% below the 7.2% baseline and under the 7.5% target' },
      ],
    },
    dimensions: [
      {
        title: 'Geo / market performance',
        lede: 'Cost-per-signup is roughly flat geo-to-geo, but monetization isn’t: Geo A converts free→paid at 3.4x the rate of Geo C/D yet is the only one budget-capped — ~$4,300/mo is parked in the worst-yielding markets.',
        columns: ['Geo', 'Spend', 'Free → paid', 'Cost / paid', 'Status'],
        rows: [
          { name: 'Geo A (budget-capped)', cells: ['$6,100', '8.9%', '$47'], status: 'Flat' },
          { name: 'Geo B', cells: ['$7,400', '5.8%', '$72'], status: 'Normal' },
          { name: 'Geo C', cells: ['$5,900', '2.6%', '$161'], status: 'Drawdown' },
          { name: 'Geo D', cells: ['$4,800', '2.9%', '$145'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Funnel / event breakdown',
        lede: 'Signups are up 14% but paid subs fell 15% — the leak is entirely the trial-to-paid step. Campaigns still bid on the free-signup event, so the networks optimize for the cheapest registration rather than a paying learner.',
        columns: ['Stage', 'Volume', 'Step rate', 'Cost / step', 'Status'],
        rows: [
          { name: 'Signup (bid event)', cells: ['6,620', '—', '$4.20'], status: 'Normal' },
          { name: 'Trial start', cells: ['1,920', '29%', '$14.5'], status: 'Declining' },
          { name: 'Paid sub', cells: ['357', '5.4%', '$78'], status: 'Drawdown' },
          { name: 'Annual upgrade', cells: ['64', '18%', '$434'], status: 'Elevated' },
        ],
      },
      {
        title: 'Creative fatigue / ad-copy',
        lede: 'Seasonal back-to-school creative is still live 6 weeks past peak and is the main drag on CTR (2.6% → 2.2%). The retargeting set is the only fresh, high-yield pool and it’s underfunded.',
        columns: ['Creative', 'Spend', 'Freq', 'CTR Δ', 'Status'],
        rows: [
          { name: 'TikTok · “Back-to-school” promo', cells: ['$5,400', '5.3', '−34%'], status: 'Drawdown' },
          { name: 'Meta · “Speak in 3 weeks” static', cells: ['$4,100', '4.7', '−22%'], status: 'Declining' },
          { name: 'Retargeting · “Streak” series', cells: ['$3,600', '2.0', '+6%'], status: 'Improving' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Rebalance geos', priority: 'P0', impact: '+~$4,300/mo redeployed to high-yield geos', body: 'Uncap Geo A to recover its 21% lost impression share; cut Geo C/D budgets that fund 2.6–2.9% free→paid traffic.' },
      { week: 'Week 2', title: 'Re-target the event', priority: 'P0', impact: 'Bidding learns on paying learners; reverses the free→paid slide', body: 'Switch bidding from free-signup to the start-trial/paid event; retire the back-to-school creative 6 weeks past peak.' },
      { week: 'Week 3', title: 'Scale retargeting', priority: 'P1', impact: '+~40 paid subs/mo from the 11.4% pool', body: 'Increase the underfunded retargeting budget (11.4% free→paid on 13% of spend); launch an annual-plan offer test.' },
      { week: 'Week 4', title: 'Lock the mix', priority: 'P2', impact: 'Holds free→paid above 6.5% at flat spend', body: 'Hold the new geo allocation and re-sequence the onboarding paywall while free-to-paid recovers above 6.5%.' },
    ],
    projection: 'Free-to-paid back toward ~7% and ~20% more paid subscriptions at the same spend within 45 days.',
  },

  // 9 — Travel / OTA --------------------------------------------------------
  {
    key: 'travel',
    industry: 'Travel',
    brand: 'Wander',
    fileName: 'wander-ads-audit.html',
    fileSize: '24.4 KB',
    meta: 'Google Ads (Search + PMax) + Meta (read-only) · Travel booking app · Last 30 days',
    healthScore: 56,
    tldr: 'Wander spends $52,400/mo at a 3.6x booking ROAS, but margin is leaking two ways: PMax is absorbing branded search demand (cannibalizing cheaper brand clicks), and cancellations aren’t fed back, so bidding overvalues routes with high cancel rates. Net booking ROAS is ~0.7x lower than reported. Health 56/100.',
    alert: {
      title: '⚠ Cancellations not fed back — bidding on gross, not net bookings',
      body: 'The networks optimize on booking confirmation, but ~14% of bookings cancel. High-cancel routes get over-funded; net ROAS is ~0.7x below the reported figure.',
    },
    kpis: [
      { label: 'Spend', value: '$52,400', delta: '+16%', bad: true },
      { label: 'Bookings (gross)', value: '4,180', delta: '+9%' },
      { label: 'Booking ROAS', value: '3.6x', delta: '-12%', bad: true },
      { label: 'Cancel rate', value: '14%', delta: '+3pt', bad: true },
      { label: 'Cost / net booking', value: '$14.6', delta: '+21%', bad: true },
      { label: 'Health', value: '56/100', delta: 'Margin leak' },
    ],
    comparison: [
      { metric: 'Net booking ROAS', current: '2.9x', previous: '3.4x', avg7: '3.1x', avg14: '3.2x', target: '3.5x', status: 'Drawdown' },
      { metric: 'Bookings (net)', current: '3,595', previous: '3,710', avg7: '3.6K', avg14: '3.65K', target: '4,000', status: 'Declining' },
      { metric: 'Cancel rate', current: '14%', previous: '11%', avg7: '13%', avg14: '12%', target: '<10%', status: 'Elevated' },
      { metric: 'Brand CPC', current: '$0.62', previous: '$0.41', avg7: '$0.55', avg14: '$0.49', target: '$0.40', status: 'Elevated' },
      { metric: 'PMax share', current: '49%', previous: '36%', avg7: '44%', avg14: '41%', target: '—', status: 'Elevated' },
      { metric: 'Wasted spend', current: '$7,400', previous: '$3,800', avg7: '$6.0K', avg14: '$5.1K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Campaign guardrails',
      columns: ['Campaign', 'Spend', 'Net ROAS', 'Status'],
      rows: [
        { name: 'Search · Brand', cells: ['$5,200', '11.4x'], status: 'Flat' },
        { name: 'PMax · All', cells: ['$25,700', '2.6x'], status: 'Drawdown' },
        { name: 'Search · Destination terms', cells: ['$12,800', '3.1x'], status: 'Declining' },
        { name: 'Meta · Retargeting', cells: ['$5,300', '6.2x'], status: 'Normal' },
        { name: 'Meta · Prospecting', cells: ['$3,400', '1.7x'], status: 'Elevated' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Feed net bookings back',
        paras: [
          'Bidding optimizes on confirmed bookings, but 14% cancel. Routes with high cancel rates therefore look profitable and get scaled. Send the cancellation/refund event so the networks bid on net, not gross — the single biggest lever here.',
        ],
      },
      {
        n: 3,
        title: 'PMax is eating brand',
        bullets: [
          'PMax share rose 13pt and brand CPC rose 51% — classic PMax-cannibalizes-brand pattern.',
          'Add brand terms as PMax account-level negatives (or use brand exclusions) to stop paying premium for owned demand.',
          'Meta Prospecting nets 1.7x — cap and shift to retargeting (6.2x).',
        ],
      },
      {
        n: 4,
        title: 'Seasonality & pacing',
        paras: [
          'Spend is up 16% into a softening demand window; pacing is flat day-of-week despite a clear weekend booking spike. Add dayparting and a demand-based pacing rule.',
        ],
      },
    ],
    trend: {
      title: 'Net booking ROAS by week',
      sub: '30-day window — the gap to gross widens as cancel rate climbs and PMax cannibalizes brand',
      points: [
        { label: 'May 5', value: 340, display: '3.4x' },
        { label: 'May 12', value: 333, display: '3.3x' },
        { label: 'May 19', value: 322, display: '3.2x' },
        { label: 'May 26', value: 312, display: '3.1x' },
        { label: 'Jun 2', value: 305, display: '3.1x' },
        { label: 'Jun 9', value: 298, display: '3.0x' },
        { label: 'Jun 16', value: 290, display: '2.9x', accent: true, tip: '<b>Jun 16</b><br>Net booking ROAS 2.9x — 0.7x below the reported gross figure and under the 3.5x target' },
      ],
    },
    dimensions: [
      {
        title: 'Wasted spend — PMax brand cannibalization',
        lede: 'PMax has no brand exclusion, so it absorbs owned-demand clicks that brand search would win for pennies. Brand CPC rose 51% ($0.41 → $0.62) as PMax share climbed 13pt — ~$7,400/mo is premium spend on demand you already own.',
        columns: ['Query / channel', 'Spend', 'Net ROAS', 'Note', 'Status'],
        rows: [
          { name: 'PMax · brand-matched queries', cells: ['$4,900', '2.4x', 'Should route to brand'], status: 'Drawdown' },
          { name: 'Meta · Prospecting', cells: ['$3,400', '1.7x', 'Below breakeven'], status: 'Elevated' },
          { name: 'Brand search (displaced)', cells: ['$5,200', '11.4x', 'Starved by PMax'], status: 'Flat' },
        ],
      },
      {
        title: 'Funnel / event breakdown',
        lede: '~14% of confirmed bookings cancel, but only the gross confirmation is fed back — so bidding pays full price for bookings that never realize. The gap between gross and net is where the 0.7x ROAS leak lives.',
        columns: ['Event', 'Volume', 'Rate', 'Fed to bidding?', 'Status'],
        rows: [
          { name: 'Booking confirmed (gross)', cells: ['4,180', '—', 'Yes'], status: 'Normal' },
          { name: 'Cancellation / refund', cells: ['585', '14%', 'No'], status: 'Elevated' },
          { name: 'Net booking realized', cells: ['3,595', '86%', 'No'], status: 'Drawdown' },
        ],
      },
      {
        title: 'Geo / route performance',
        lede: 'High-cancel routes look profitable on gross and keep getting scaled. Once cancellations are netted out, two route clusters fall well below the 3.5x target while low-cancel destinations carry the account.',
        columns: ['Route cluster', 'Spend', 'Cancel %', 'Net ROAS', 'Status'],
        rows: [
          { name: 'Short-haul city breaks', cells: ['$13,600', '9%', '3.8x'], status: 'Normal' },
          { name: 'Long-haul flexible fares', cells: ['$18,200', '21%', '2.4x'], status: 'Drawdown' },
          { name: 'Last-minute packages', cells: ['$11,400', '18%', '2.6x'], status: 'Declining' },
          { name: 'Retargeting (all routes)', cells: ['$5,300', '7%', '6.2x'], status: 'Improving' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Send cancellations', priority: 'P0', impact: 'Recovers the ~0.7x net-vs-gross ROAS gap', body: 'Instrument the cancel/refund event; switch eligible campaigns to net-booking value so high-cancel routes stop being over-funded.' },
      { week: 'Week 2', title: 'Protect brand', priority: 'P0', impact: '+~$7,400/mo recovered; brand CPC down ~40%', body: 'Add brand exclusions to PMax to stop paying premium for owned demand; cap Meta Prospecting (1.7x net).' },
      { week: 'Week 3', title: 'Pacing', priority: 'P1', impact: 'Aligns spend to the weekend booking spike', body: 'Add dayparting + demand-based weekend pacing; reallocate to retargeting (6.2x) + destination terms.' },
      { week: 'Week 4', title: 'Rescale on net', priority: 'P2', impact: 'Net booking ROAS toward ~3.4x at controlled spend', body: 'Scale only the route clusters that hold net ROAS above 3.2x; hold back high-cancel long-haul.' },
    ],
    projection: 'Net booking ROAS back toward ~3.4x and brand CPC down ~40% within 30 days.',
  },

  // 10 — Media / Streaming (short drama) ------------------------------------
  {
    key: 'media',
    industry: 'Media & Streaming',
    brand: 'Dramaverse',
    fileName: 'dramaverse-ads-audit.html',
    fileSize: '25.0 KB',
    meta: 'Meta Ads + TikTok + Google (UAC) (read-only) · Short-drama streaming app · Last 30 days',
    healthScore: 52,
    tldr: 'Dramaverse spends $58,900/mo on aggressive UA at a $1.95 install cost, but the model lives or dies on first-week purchases of episode unlocks — and D7 ROAS fell from 0.46x to 0.31x as creative fatigue set in and TikTok broad scaled. Cost-per-paying-viewer is up 38%. The winning hook format is under-produced. Health 52/100.',
    alert: {
      title: '⚠ D7 ROAS 0.31x vs 0.50x target — one hook format carries everything',
      body: 'A single “cliffhanger” hook format drives 71% of purchases but only 19% of creative output. The rest of the spend buys installs that rarely unlock a paid episode.',
    },
    kpis: [
      { label: 'Spend', value: '$58,900', delta: '+27%', bad: true },
      { label: 'Installs', value: '30,200', delta: '+24%' },
      { label: 'CPI', value: '$1.95', delta: '+6%', bad: true },
      { label: 'Paying-viewer %', value: '3.8%', delta: '-22%', bad: true },
      { label: 'D7 ROAS', value: '0.31x', delta: '-33%', bad: true },
      { label: 'Health', value: '52/100', delta: 'Creative-bound' },
    ],
    comparison: [
      { metric: 'D7 ROAS', current: '0.31x', previous: '0.46x', avg7: '0.36x', avg14: '0.40x', target: '0.50x', status: 'Drawdown' },
      { metric: 'CPI', current: '$1.95', previous: '$1.84', avg7: '$1.9', avg14: '$1.87', target: '$1.80', status: 'Elevated' },
      { metric: 'Paying viewers', current: '1,148', previous: '1,310', avg7: '1.2K', avg14: '1.26K', target: '1,500', status: 'Declining' },
      { metric: 'Cost / payer', current: '$51', previous: '$37', avg7: '$45', avg14: '$42', target: '$38', status: 'Critical' },
      { metric: 'CTR', current: '1.1%', previous: '1.7%', avg7: '1.3%', avg14: '1.5%', target: '—', status: 'Declining' },
      { metric: 'Wasted spend', current: '$9,200', previous: '$4,600', avg7: '$7.3K', avg14: '$6.1K', target: '$0', status: 'Critical' },
    ],
    breakdown: {
      title: 'Channel guardrails',
      columns: ['Source', 'Spend', 'D7 ROAS', 'Status'],
      rows: [
        { name: 'TikTok · Cliffhanger hooks', cells: ['$14,200', '0.58x'], status: 'Normal' },
        { name: 'TikTok · Broad', cells: ['$16,800', '0.14x'], status: 'Drawdown' },
        { name: 'Meta · Lookalike payers', cells: ['$13,400', '0.44x'], status: 'Declining' },
        { name: 'UAC · In-app purchase', cells: ['$9,900', '0.39x'], status: 'Declining' },
        { name: 'UAC · Installs', cells: ['$4,600', '0.09x'], status: 'Drawdown' },
      ],
    },
    sections: [
      {
        n: 2,
        title: 'Concentrate on the winning hook',
        paras: [
          'The cliffhanger hook format returns 0.58x D7 ROAS and drives 71% of purchases, but it’s only 19% of creative output. Everything else is dragging the blended number down. The fastest win is to produce more of the format that already works and stop diluting spend across weak formats.',
        ],
      },
      {
        n: 3,
        title: 'Cut volume-buying channels',
        bullets: [
          'TikTok Broad (0.14x) and UAC Installs (0.09x) together burn ~$9,200/mo for almost no payers.',
          'Move both budgets into cliffhanger hooks + lookalike-payers.',
          'Switch in-app campaigns to purchase-value bidding now that payer signal is sufficient.',
        ],
      },
      {
        n: 4,
        title: 'Creative velocity',
        paras: [
          'CTR fell from 1.7% to 1.1% as the top hooks crossed frequency 6. At this spend you need a steady stream of cliffhanger variants — set up a weekly batch and retire anything past frequency 5.',
        ],
      },
    ],
    trend: {
      title: 'D7 ROAS by week',
      sub: '30-day window — the drop tracks +27% spend poured into TikTok Broad while the cliffhanger hook stayed under-produced',
      points: [
        { label: 'May 5', value: 46, display: '0.46x' },
        { label: 'May 12', value: 44, display: '0.44x' },
        { label: 'May 19', value: 41, display: '0.41x' },
        { label: 'May 26', value: 38, display: '0.38x' },
        { label: 'Jun 2', value: 35, display: '0.35x' },
        { label: 'Jun 9', value: 33, display: '0.33x' },
        { label: 'Jun 16', value: 31, display: '0.31x', accent: true, tip: '<b>Jun 16</b><br>D7 ROAS 0.31x — 33% below baseline and well under the 0.50x target' },
      ],
    },
    dimensions: [
      {
        title: 'Creative fatigue / hook format',
        lede: 'A single “cliffhanger” hook drives 71% of purchases but is only 19% of output. The winning format is at frequency 6+ with no fresh variants, while diluted formats burn impressions at a fraction of the payer rate.',
        columns: ['Hook format', 'Spend share', 'Purchase share', 'Freq', 'Status'],
        rows: [
          { name: 'Cliffhanger hook', cells: ['24%', '71%', '6.2'], status: 'Drawdown' },
          { name: 'Slow-burn intro', cells: ['31%', '14%', '4.1'], status: 'Declining' },
          { name: 'Cast montage', cells: ['28%', '9%', '3.7'], status: 'Elevated' },
          { name: 'Generic trailer cut', cells: ['17%', '6%', '5.4'], status: 'Critical' },
        ],
      },
      {
        title: 'Wasted spend — volume-buying channels',
        lede: '~$9,200/mo flows to channels that buy installs Google and TikTok can find cheaply but that almost never unlock a paid episode — TikTok Broad and UAC Installs return 0.14x and 0.09x.',
        columns: ['Channel', 'Spend', 'D7 ROAS', 'Paying-viewer %', 'Status'],
        rows: [
          { name: 'TikTok · Broad', cells: ['$16,800', '0.14x', '1.1%'], status: 'Drawdown' },
          { name: 'UAC · Installs', cells: ['$4,600', '0.09x', '0.8%'], status: 'Critical' },
          { name: 'TikTok · Cliffhanger hooks', cells: ['$14,200', '0.58x', '6.4%'], status: 'Normal' },
        ],
      },
      {
        title: 'Funnel / event breakdown',
        lede: 'Installs are up 24% but paying viewers fell 12% — the install bump never reaches the episode-unlock event. In-app campaigns still bid on install/registration rather than purchase value, so the networks can’t find payers.',
        columns: ['Event', 'Volume', 'Step rate', 'Cost / step', 'Status'],
        rows: [
          { name: 'Install (bid event)', cells: ['30,200', '—', '$1.95'], status: 'Elevated' },
          { name: 'First episode opened', cells: ['12,680', '42%', '$4.6'], status: 'Declining' },
          { name: 'Paying viewer (unlock)', cells: ['1,148', '3.8%', '$51'], status: 'Drawdown' },
        ],
      },
    ],
    plan: [
      { week: 'Week 1', title: 'Cut volume buys', priority: 'P0', impact: '+~$9,200/mo recovered from 0.09–0.14x channels', body: 'Pause TikTok Broad + UAC Installs — together they burn ~$9,200/mo for almost no payers.' },
      { week: 'Week 2', title: 'Bid on value', priority: 'P0', impact: 'Networks optimize for payers, not cheap installs', body: 'Send purchase value; switch in-app campaigns to value-based bidding now that payer signal is sufficient.' },
      { week: 'Week 3', title: 'Scale the hook', priority: 'P1', impact: 'Lifts blended D7 ROAS toward the 0.58x hook benchmark', body: 'Triple cliffhanger-format output (currently 19% of creative, 71% of purchases); reallocate freed budget into it + lookalike-payers.' },
      { week: 'Week 4', title: 'Hold ROAS', priority: 'P2', impact: 'Sustains D7 ROAS ~0.48x; cost-per-payer down ~25%', body: 'Rescale only while D7 ROAS holds above 0.45x; retire any creative past frequency 5 each week.' },
    ],
    projection: 'D7 ROAS back toward ~0.48x and cost-per-payer down ~25% within 30 days.',
  },
]

export const DEFAULT_REPORT_KEY = 'ecommerce'
